import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Button,
  Badge,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import EstablishmentForm from "./EstablishmentForm";
import PointsManager from "./PointsManager";
import AssignmentAlert from "./AssignmentAlert";
import { getEstablishments } from "../../../../slices/companies/thunk";
import { hydrateAssignmentsFromToken } from "../../../../slices/assignments";

const EstablishmentsManager = ({ establishments, onSave, onDelete, isSaving, executeWithPasswordValidation, companyId }: any) => {
  const dispatch = useDispatch();
  const { currentEstablishment, currentPoint } = useSelector((state: any) => state.Assignment);
  const [isCreating, setIsCreating] = useState(false);
  const [editingEstablishment, setEditingEstablishment] = useState(null);
  const [selectedEstForPoints, setSelectedEstForPoints] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [establishmentToDelete, setEstablishmentToDelete] = useState<string | null>(null);

  useEffect(() => {
      dispatch(getEstablishments(companyId) as any);
  }, [dispatch, companyId]);

  useEffect(() => {
      dispatch(hydrateAssignmentsFromToken() as any);
  }, [dispatch]);

  const handleAdd = () => {
    setEditingEstablishment(null);
    setIsCreating(true);
  };

  const handleEdit = (est: any) => {
    setEditingEstablishment(est);
    setIsCreating(false);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingEstablishment(null);
  };

  const onClickDelete = (id: string) => {
    setEstablishmentToDelete(id);
    setDeleteModal(true);
  };

  const confirmDelete = () => {
    if (establishmentToDelete) {
        onDelete(establishmentToDelete);
        setDeleteModal(false);
        setEstablishmentToDelete(null);
    }
  };

  const handleSave = (values: any) => {
    const payload = editingEstablishment ? { ...values, id: (editingEstablishment as any).id } : values;
    onSave(payload, !!editingEstablishment, () => {
        handleCancel();
    });
  };

  
  if (selectedEstForPoints) {
    return <PointsManager establishment={selectedEstForPoints} onBack={() => setSelectedEstForPoints(null)} executeWithPasswordValidation={executeWithPasswordValidation} />;
  }

  if (isCreating || editingEstablishment) {
      return <EstablishmentForm establishment={editingEstablishment} isCreating={isCreating} onSubmit={handleSave} onCancel={handleCancel} isSaving={isSaving} />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Establecimientos</h5>
        <Button color="primary" size="sm" onClick={handleAdd}><i className="ri-add-line align-bottom"></i> Agregar</Button>
      </div>
      
      <AssignmentAlert
        currentEstablishment={currentEstablishment}
        currentPoint={currentPoint}
        establishments={establishments}
        onAddEstablishment={handleAdd}
        onSelectEstForPoints={setSelectedEstForPoints}
      />
      
      {establishments.length > 0 ? (
        <div className="vstack gap-2">
          {establishments.map((est: any) => (
            <Card key={est.id} className="mb-0 border shadow-none">
              <CardBody className="p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0 me-3">
                      <div className="avatar-title bg-light text-primary opacity-75 rounded-circle fs-4">
                        {est.code}
                      </div>
                    </div>
                    <div>
                      <h5 className="fs-14 mb-1 fw-semibold">
                        Establecimiento {est.code}
                      </h5>

                      <p className="text-muted mb-2">
                        <i className="ri-map-pin-line me-1"></i> {est.address}
                      </p>

                      {String(currentEstablishment ?? '') === String(est.id ?? '') && (
                        <Badge color="success" className="mb-2">
                          <i className="bx bx-check-circle me-1"></i>
                          Asignado
                        </Badge>
                      )}

                      <div className="d-flex justify-content-start">
                        <Button
                            color="primary"
                            size="sm"
                            title="Ver o agregar puntos de emisión"
                            className="btn waves-effect waves-light btn-hover-effect"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEstForPoints(est);
                            }}
                        >
                          <i className="ri-add-line align-bottom"></i>
                          Puntos de Emisión
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                        color="warning"
                        size="sm"
                        title="Editar"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(est);
                        }}
                    >
                      <i className="ri-pencil-fill"></i>
                    </Button>

                    <Button
                        color="danger"
                        size="sm"
                        title="Eliminar"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickDelete(est.id);
                        }}
                    >
                      <i className="ri-delete-bin-fill"></i>
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-4">
          <p>No hay establecimientos para esta compañía.</p>
        </div>
      )}
      
      <DeleteModal show={deleteModal} onDeleteClick={confirmDelete} onCloseClick={() => setDeleteModal(false)} />
    </div>
  );
};

export default EstablishmentsManager;
